import React, { FC } from 'react';
import { CoreMenuItem, Item } from './types';
import { SubMenu } from './sub-menu';
import { HStack, Box, Menu } from '@chakra-ui/react';
import { Icon } from '../Icon';
import { Image } from '../Image';
import { Badge as CoreBadge, BadgeVariant } from '../Badge';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { kebabCase } from 'lodash';

export const MenuItem: FC<CoreMenuItem> = ({
  item,
  isMenuItemOptions,
  isChecked,
  onClick,
  onCheckedChange,
  type = 'checkbox',
  value,
}) => {
  const hasSubmenu = item.items && item.items.length > 0;
  // `text` widened to ReactNode, so it only stands in for a value when it is a plain string.
  const itemValue = item.value ?? item.id ?? (typeof item.text === 'string' ? item.text : '');

  if (hasSubmenu) {
    const currentItem = {
      itemConfig: item.itemConfig,
      text: item.text,
      value: item.value,
      disabled: item.disabled,
      submenuConfig: item.submenuConfig,
      ariaLabel: item.ariaLabel,
      dataAction: item.dataAction,
      dataObjectId: item.dataObjectId,
      dataObjectType: item.dataObjectType,
    } as Item;
    return <SubMenu menuItem={currentItem} items={item.items ?? []} isMenuItemOptions={isMenuItemOptions} />;
  }
  if (isMenuItemOptions) {
    const optionProps = {
      value: value ?? itemValue,
      disabled: item.disabled,
      closeOnSelect: item.closeOnSelect ?? false,
      onClick,
      'aria-label': item.ariaLabel ?? (typeof item.text === 'string' ? item.text : undefined),
      'data-action': item.dataAction,
      'data-object-id': item.dataObjectId,
      'data-object-type': item.dataObjectType,
    };
    const optionBody = (
      <>
        <RegularMenuItem item={item} />
        <Menu.ItemIndicator />
      </>
    );
    // RadioItem reads its checked state from the enclosing RadioItemGroup; CheckboxItem owns its own.
    return type === 'radio' ? (
      <Menu.RadioItem {...optionProps}>{optionBody}</Menu.RadioItem>
    ) : (
      <Menu.CheckboxItem {...optionProps} checked={!!isChecked} onCheckedChange={onCheckedChange}>
        {optionBody}
      </Menu.CheckboxItem>
    );
  }
  if (item.isDivider) {
    return <Menu.Separator />;
  }
  if (item.itemConfig?.href) {
    return (
      <Menu.Item value={itemValue} asChild>
        <a data-testid={item.id} onClick={item.onClick} href={item.itemConfig?.href}>
          {!!item.text && <> {item.text} </>}
        </a>
      </Menu.Item>
    );
  }
  return (
    <Menu.Item
      value={itemValue}
      data-testid={item.id}
      data-action={item.dataAction ?? (typeof item.text === 'string' ? kebabCase(item.text) : undefined)}
      data-object-id={item.dataObjectId}
      data-object-type={item.dataObjectType}
      onClick={item.onClick}
      closeOnSelect={item.closeOnSelect ?? !hasSubmenu}
      aria-label={item.ariaLabel}
      disabled={item.disabled}
    >
      {item.itemConfig?.iconName && (
        <Icon
          size={item.itemConfig?.iconSize}
          color={item.itemConfig?.iconColorScheme}
          prefix={item.itemConfig?.iconFamily}
          iconName={item.itemConfig?.iconName as IconName}
        />
      )}
      <RegularMenuItem item={item} />
    </Menu.Item>
  );
};

export const RegularMenuItem: FC<CoreMenuItem> = ({ item, selectionCount }) => {
  return (
    <HStack>
      {!!item.itemConfig?.imagePath && (
        <Box>
          <Image path={item.itemConfig?.imagePath} width={'32px'} height={'32px'} />
        </Box>
      )}
      <Box flexGrow={1}>
        {!!item.text && item.text}
        {!!item.itemConfig?.description && (
          <Box as="small" display="block">
            {item.itemConfig?.description}
          </Box>
        )}
      </Box>
      {selectionCount && (
        <CoreBadge colorScheme="neutral" variant={BadgeVariant.subtle}>
          {selectionCount}
        </CoreBadge>
      )}
      {!!item.itemConfig?.rightIconName && <Icon size={'sm'} iconName={item.itemConfig?.rightIconName} />}
    </HStack>
  );
};
